import {
  Environment,
  FeatureFlagType,
  FeatureVariableResultType,
  VariableType,
} from '@kameleoon/javascript-sdk';

/**
 * Kameleeon Goal ID mapping
 */
export const enum KameleeonGoal {
  IntroScreenButtonClick = 326241, // Only here as an example. The goal is automatically tracked by Kameleoon.
  InsuranceInformationSaved = 332350,
  MainReasonForVisitScreenReached = 333150,
  ReviewScreenCompleted = 334029,
}

const overrideEnv = process.env.NEXT_PUBLIC_EXP_OVERRIDE_ENV; // For overriding the Kameleeon environment to use (e.g., for E2E testing)
const appEnv = process.env.NEXT_PUBLIC_DD_ENV;
const expEnv = overrideEnv ?? appEnv;

export const getConfiguration = () => ({
  updateInterval: 1,
  environment:
    expEnv === 'prod'
      ? Environment.Production
      : expEnv === 'staging'
      ? Environment.Staging
      : Environment.Development,
  domain:
    expEnv === 'prod'
      ? 'app.talkiatry.com'
      : appEnv === 'staging'
      ? 'app.staging.talkiatry.com'
      : 'localhost',
});

export type DepsProvider = {
  getClient: () => Promise<any>; // TODO: Had to use any here because the types are not lining up
  getVisitorCode: () => Promise<string>;
};

type VariationVariableValues = Record<string, FeatureVariableResultType>;
export type FlagState = VariationVariableValues | boolean;

export const getFlagsForUser = async (
  depsProvider: DepsProvider
): Promise<FeatureFlagType[]> => {
  try {
    const kameleoonClient = await depsProvider.getClient();
    return kameleoonClient.getVisitorFeatureFlags(
      await depsProvider.getVisitorCode()
    );
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getFlagStateForUser = async (
  depsProvider: DepsProvider,
  flagName: string,
  variables?: string[]
): Promise<FlagState> => {
  try {
    const kameleoonClient = await depsProvider.getClient();
    const visitorCode = await depsProvider.getVisitorCode();
    if (!kameleoonClient.isFeatureFlagActive(visitorCode, flagName)) {
      return false;
    }

    const flagValue = kameleoonClient.getFeatureFlagVariables(
      visitorCode,
      flagName
    );

    if (flagValue === 'off') {
      // default "off" value, unless overridden
      return false;
    }

    if (variables && variables.length > 0) {
      const flagState: Record<string, FeatureVariableResultType> = {};
      variables.forEach(variable => {
        let variableValue: FeatureVariableResultType = {
          value: '',
          type: VariableType.STRING,
        }; // Default to empty string so that code won't break due to mistakes in Kameleeon
        try {
          variableValue = kameleoonClient.getFeatureFlagVariable({
            visitorCode,
            featureKey: flagName,
            variableKey: variable,
          });
        } catch (error) {
          console.error(error);
        } finally {
          flagState[variable] = variableValue;
        }
      });
      return flagState;
    }

    return flagValue;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const trackConversion = async (
  depsProvider: DepsProvider,
  goal: KameleeonGoal
) => {
  const kameleoonClient = await depsProvider.getClient();
  const visitorCode = await depsProvider.getVisitorCode();

  kameleoonClient.trackConversion({ visitorCode, goalId: goal });
};

// Helper function for getting the value of a feature variable from a flag state
export const getFlagStateValue = (flagState: FlagState, variable: string) =>
  flagState && flagState[variable]?.value;
